import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

// Shipping carriers configuration
export const SHIPPING_CARRIERS = {
  mrw: {
    id: 'mrw',
    name: 'MRW',
    description: 'Envío estándar a domicilio',
    price: 6.60,
    icon: '🚚',
    deliveryTime: '2-3 días'
  },
  seur: {
    id: 'seur',
    name: 'SEUR',
    description: 'SEUR 24h a domicilio',
    price: 6.72,
    icon: '📦',
    deliveryTime: '24-48 horas'
  }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('jrf-cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [coupon, setCoupon] = useState(() => {
    const saved = localStorage.getItem('jrf-coupon');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedCarrier, setSelectedCarrier] = useState(() => {
    const saved = localStorage.getItem('jrf-carrier');
    return SHIPPING_CARRIERS[saved] ? saved : 'mrw';
  });
  const [liveRates, setLiveRates] = useState(null);
  const [liveCarrier, setLiveCarrier] = useState(null);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('jrf-cart', JSON.stringify(cart));
  }, [cart]);

  // Save coupon to localStorage whenever it changes
  useEffect(() => {
    if (coupon) {
      localStorage.setItem('jrf-coupon', JSON.stringify(coupon));
    } else {
      localStorage.removeItem('jrf-coupon');
    }
  }, [coupon]);

  // Save carrier to localStorage
  useEffect(() => {
    localStorage.setItem('jrf-carrier', selectedCarrier);
  }, [selectedCarrier]);

  const addToCart = (garment, size, color, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(
        item => item.garment_id === garment.id && item.size === size && item.color === color
      );

      if (existingItem) {
        return prevCart.map(item =>
          item.garment_id === garment.id && item.size === size && item.color === color
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [
        ...prevCart,
        {
          garment_id: garment.id,
          name: garment.name,
          price: garment.price,
          quantity,
          size,
          color,
          image_url: garment.image_url || (Array.isArray(garment.images) && garment.images[0]) || ''
        }
      ];
    });
  };

  const removeFromCart = (garmentId, size, color) => {
    setCart(prevCart =>
      prevCart.filter(
        item => !(item.garment_id === garmentId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (garmentId, size, color, quantity) => {
    if (quantity <= 0) {
      removeFromCart(garmentId, size, color);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.garment_id === garmentId && item.size === size && item.color === color
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setCoupon(null);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getShippingCost = () => {
    const total = getCartTotal();
    
    // Free shipping if coupon is free_shipping type
    if (coupon && coupon.discount_type === 'free_shipping') {
      return 0;
    }
    
    // Free shipping over 80€ for all carriers
    if (total >= 80) {
      return 0;
    }
    
    // Get carrier price
    if (liveCarrier) {
      return liveCarrier.price;
    }
    const carrier = SHIPPING_CARRIERS[selectedCarrier];
    return carrier ? carrier.price : 4.99;
  };

  const selectCarrier = (carrierId) => {
    if (SHIPPING_CARRIERS[carrierId]) {
      setSelectedCarrier(carrierId);
      setLiveCarrier(null);
    }
  };

  const selectLiveCarrier = (rate) => {
    setLiveCarrier(rate);
  };

  const setRates = (rates) => {
    setLiveRates(rates);
  };

  const getSelectedCarrier = () => {
    if (liveCarrier) {
      return {
        id: liveCarrier.id,
        name: liveCarrier.carrier,
        carrier: liveCarrier.carrier,
        serviceName: liveCarrier.service_name,
        packlinkServiceId: liveCarrier.packlink_service_id,
        source: liveCarrier.source,
        description: liveCarrier.service_name,
        price: liveCarrier.price,
        icon: '📦',
        deliveryTime: liveCarrier.delivery_time || '2-4 días'
      };
    }
    return SHIPPING_CARRIERS[selectedCarrier] || SHIPPING_CARRIERS.mrw;
  };

  const applyCoupon = (couponData) => {
    setCoupon(couponData);
  };

  const removeCoupon = () => {
    setCoupon(null);
  };

  // Get final total with all discounts
  const getFinalTotal = () => {
    const subtotal = getCartTotal();
    const shipping = getShippingCost();
    let discount = 0;
    
    if (coupon) {
      if (coupon.discount_type === 'free_shipping') {
        // Shipping is already 0 from getShippingCost
        discount = 0;
      } else if (coupon.discount_type === 'affiliate' || coupon.discount_type === 'percentage') {
        discount = coupon.discount_amount || 0;
      } else if (coupon.discount_type === 'fixed_amount') {
        discount = coupon.discount_amount || 0;
      }
    }
    
    return subtotal + shipping - discount;
  };

  const value = {
    cart,
    coupon,
    selectedCarrier,
    liveRates,
    liveCarrier,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    getShippingCost,
    getFinalTotal,
    applyCoupon,
    removeCoupon,
    selectCarrier,
    selectLiveCarrier,
    setRates,
    getSelectedCarrier
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
